'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { useError } from './ErrorContext'

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
  }
}

async function getOrCreateAnonymizedIP(): Promise<string | null> {
  // Try to get cached IP from localStorage
  const cachedIP = localStorage.getItem('anonymized_ip')
  if (cachedIP) {
    return cachedIP
  }

  try {
    // Fetch anonymized IP from API
    const response = await fetch('/api/session/ip')
    const data = await response.json()
    const ip = data.ip

    // Cache the IP in localStorage
    if (ip) {
      localStorage.setItem('anonymized_ip', ip)
    }

    return ip
  } catch (error) {
    console.error('Failed to fetch anonymized IP:', error)
    return null
  }
}

export default function PageMeasurement() {
  const pathname = usePathname()
  const { error } = useError()

  useEffect(() => {
    // Push page measurement event to dataLayer
    const pushPageEvent = async () => {
      if (!window.dataLayer) return

      // Extract page title without the generic part
      let pageTitle = document.title
      if (pageTitle.includes(' | ')) {
        pageTitle = pageTitle.split(' | ')[0]
      }

      // Ensure IP is initialized before first page event
      const anonymizedIP = await getOrCreateAnonymizedIP()

      // Determine page type
      const getPageType = (): string | null => {
        if (error) return 'error'
        if (pathname === '/') return 'home'
        return null
      }

      // Build page object - always set type to prevent stale values
      const pageData: Record<string, unknown> = {
        currencyCode: 'EUR',
        locale: 'en-US',
        title: pageTitle,
        fullPath: pathname,
        type: getPageType(),
        error: null,
      }

      // Add error data for error pages (403, 404, 50x, etc.)
      if (error) {
        pageData.error = {
          type: error.type,
          message: error.message,
        }
      }

      // Push page info with session data FIRST
      window.dataLayer.push({
        page: pageData,
        session: {
          ip: anonymizedIP,
          machine: null,
          application: null,
        },
      })

      // Then push the page trigger
      window.dataLayer.push({ event: 'page' })
    }

    pushPageEvent()
  }, [pathname, error])

  return null
}
